/**
 * Shared renderers for Freshdesk Solutions article rows - the market / status chips,
 * the Category > Folder breadcrumb and the open-in-Freshdesk link. Promoted from
 * `FreshdeskSolutionsTab` so the Solutions tab and the `SolutionsBrowser` render
 * articles identically (shared-first rule). `getMarketChipColor` already lives in
 * `utils/marketChip`.
 */
import React from 'react'
import { Box, Chip, IconButton, Link, Tooltip, Typography } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'

import { getMarketChipColor } from '../../../utils/marketChip'

/** Market pill (ANZ / USA). */
export const marketChip = (market: string): React.ReactNode => (
  <Chip label={market} size="small" color={getMarketChipColor(market)} />
)

/** Draft (1) vs Published (2) status pill. */
export const statusChip = (status: number): React.ReactNode => (
  <Chip
    size="small"
    variant="outlined"
    color={status === 1 ? 'default' : 'success'}
    label={status === 1 ? 'Draft' : 'Published'}
  />
)

/** Open-in-Freshdesk icon button (new tab). */
export const openLink = (url: string): React.ReactNode => (
  <Tooltip title="Open in Freshdesk">
    <IconButton
      size="small"
      component={Link}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
    >
      <OpenInNewIcon fontSize="small" />
    </IconButton>
  </Tooltip>
)

/**
 * Inline breadcrumb: muted category, chevron, folder. Returns '-' when both empty.
 * Pass `{ icons: true }` to prefix the category / folder with their tree + folder
 * icons (used under the article name in the Solutions Browser rows).
 */
export const categoryFolder = (
  category?: string | null,
  folder?: string | null,
  options?: { icons?: boolean },
): React.ReactNode => {
  if (!category && !folder) return '-'
  const withIcons = options?.icons ?? false
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
      {category && (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
          {withIcons && (
            <AccountTreeOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          )}
          <Typography variant="body2" component="span" color="text.secondary">
            {category}
          </Typography>
        </Box>
      )}
      {category && folder && (
        <Typography variant="body2" component="span" color="text.disabled">
          ›
        </Typography>
      )}
      {folder && (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
          {withIcons && (
            <FolderOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
          )}
          <Typography variant="body2" component="span">
            {folder}
          </Typography>
        </Box>
      )}
    </Box>
  )
}
